import { Grid, Tab, Tabs } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, Route, Switch, useParams, useRouteMatch } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { EEScorecardService } from "../../Component/Api/EmploymentEquity/Scorecard/EEScorecardService";
import { EmploymentEquityDashboard } from "../../Component/Common/Components/Dashboard/EmploymentEquity/EmploymentEquityDashboard";
import { MDBox } from "../../Component/Common/Components/MDBox";
import { MDTypography } from "../../Component/Common/Components/MDTypography";
import { eeScorecard } from "../../Component/Common/Constants/routes";
import { EE123SalariesRole } from "../../Component/Common/Constants/UserRoles";
import { ConsolidationAtom } from "../../State/Consolidation.Atom";
import { EEScorecardAtom } from "../../State/EEScorecard.Atom";
import { ScorecardAtom } from "../../State/Scorecard.Atom";
import { userRolesState } from "../../State/user.Atom";
import { IDropDownOptions } from "../../Component/Common/Components/FormValidationInputs/AppSelectList";
import { DashboardService } from "../../Component/Api/EmploymentEquity/Dashboard/DashboardService";
import moment from "moment";


export const DashboardBasePage = () => {

    const { scorecardId } = useParams<{ scorecardId: string }>();
    const setScorecardId = useSetRecoilState(ScorecardAtom);
    const setScorecardType = useSetRecoilState(ConsolidationAtom);
    const [scorecard, setScorecard] = useRecoilState(EEScorecardAtom);
    const [visible, setVisible] = useState(false);
    const [sourceScorecardLookups, setSourceScorecardLookups] = useState<IDropDownOptions[] | undefined>([]);
    let isConsolidatedScorecard = false;
    const [currentDate] = useState(() => new Date());
    const eePlanDateFrom = new Date(scorecard.EEPlanDateFrom);
    const nextPlanStart = new Date(scorecard.EEPlanDateFrom);
    nextPlanStart.setFullYear(nextPlanStart.getFullYear() + 1)
    const eePlanDateListTemp: { id: string; value: string }[] = [];
    const currentFrom = new Date(eePlanDateFrom);       
    const currentStart = new Date(nextPlanStart);
    const eePlanYears = scorecard.EEPlanYears;
    const [eePlanDateList, setEEPlanDateList] = useState<IDropDownOptions[]>([]);

    const fetchData = async (scorecardId: string) => {
        await Promise.all([
            getScorecardType(scorecardId),
            getScoreCard(scorecardId)]
        ).then(() => {
            getConsolidatedScorecardEEPlanSources(scorecardId);
            setVisible(true);
        });
    }

    useEffect(() => {
        setScorecardId({ scorecardId: scorecardId });
        fetchData(scorecardId);
        populateEEPlanDateList();
    }, [scorecardId])

    const getScorecardType = async (scorecardId: string) => {
        try {
            const scorecardType = await EEScorecardService.CheckScorecardType(scorecardId);
            setScorecardType(scorecardType.data);
            isConsolidatedScorecard = scorecardType.data;
        } catch (error) {
            console.log(error)
        }
    }

    const getScoreCard = async (scorecardId: string) => {
        try {
            let result = await EEScorecardService.GetEEGeneralInfo(scorecardId);
            setScorecard(result.data);

        } catch (error) {
            console.log(error)
        }
    }

    const getConsolidatedScorecardEEPlanSources = async (scorecardId: string) => {
        if(isConsolidatedScorecard){
            try {
                const result = await EEScorecardService.GetConsolidationRulesMap(scorecardId);
                const sourceLookups = await DashboardService.GetSourceScorecardLookups(scorecardId);
                setSourceScorecardLookups(sourceLookups.data);
                const sourceScorecardsCount = result.data.sourceScorecardIds?.length ?? 0;
                
                if (sourceScorecardsCount > 0) {
                    setScorecard(prevScorecard => ({
                    ...prevScorecard,
                    ConsolidationSourceScorecardsCount: sourceScorecardsCount,
                    })); 
                }
            } 
            catch (error) {
                console.log(error)
            }
        }
    }


    const routes = [
        `/ee/scorecard/${scorecardId}/dashboard/${eeScorecard.employeeHeadcountDashboard}`,
        `/ee/scorecard/${scorecardId}/dashboard/${eeScorecard.incomeDifferentialsDashboard}`,
        `/ee/scorecard/${scorecardId}/dashboard/${eeScorecard.eePlanTrackingDashboard}`,
    ]

    const routeMatch = useRouteMatch(routes);
    const currentTab = routeMatch?.path;

    const restrictedUsers = [
        EE123SalariesRole
    ];

    const userRoles = useRecoilValue(userRolesState);

    const populateEEPlanDateList = () => {
        for (let i = 1; i <= eePlanYears; i++) {
            const eePlanDateTo = new Date(currentStart);
            eePlanDateTo.setDate(eePlanDateTo.getDate() - 1);

            const formattedFrom = moment(currentFrom).format('D MMMM YYYY');
            const formattedTo = moment(eePlanDateTo).format('D MMMM YYYY');

            eePlanDateListTemp.push({
            id: `${i}`,
            value: `Year ${i} (${formattedFrom} - ${formattedTo})`,
            });

            currentFrom.setFullYear(currentFrom.getFullYear() + 1);
            currentStart.setFullYear(currentStart.getFullYear() + 1);
        }
        setEEPlanDateList(eePlanDateListTemp);
    }

    return (
        <>
        { visible &&
            <>
                <MDBox style={{ padding: "10px" }}>
                    <MDTypography variant='h4' fontWeight='bold' style={{ paddingBottom: "15px" }}>
                        Dashboard
                    </MDTypography>
                </MDBox>
                <Grid container>
                    <Grid item xs={12}>
                        <Tabs value={currentTab} aria-label="Tabs" centered>
                            <Tab label="Employee Headcount" value={routes[0]} to={routes[0]} component={Link} />
                            {userRoles.some(r => restrictedUsers.includes(r)) && <Tab label="Income Differentials" value={routes[1]} to={routes[1]} component={Link} />}
                            <Tab label="EE Plan Tracking" value={routes[2]} to={routes[2]} component={Link} />
                        </Tabs>
                    </Grid>
                    <Grid item xs={12} mt={2}>
                        <Switch>
                            <Route path={routes[0]}>
                                <EmploymentEquityDashboard
                                    isVisible={true}
                                    scorecardId={scorecardId}
                                    scorecardTypeId={"43"}
                                    isPowerPointPrint={false}
                                    isStacked={true}
                                    scorecard={scorecard}
                                    isIncomeDifferentialDashboard={false}
                                    sourceScorecardLookups={sourceScorecardLookups}
                                    isEEPlanTrackingDashboard={false}
                                    currentDate={currentDate}
                                />
                            </Route>
                            <Route path={routes[1]}>
                                <EmploymentEquityDashboard
                                    isVisible={true}
                                    scorecardId={scorecardId}
                                    scorecardTypeId={"43"}
                                    isPowerPointPrint={false}
                                    isStacked={true}
                                    scorecard={scorecard}
                                    isIncomeDifferentialDashboard={true}
                                    sourceScorecardLookups={sourceScorecardLookups}
                                    isEEPlanTrackingDashboard={false}
                                    currentDate={currentDate}
                                />
                            </Route>
                             <Route path={routes[2]}>
                                <EmploymentEquityDashboard
                                    isVisible={true}
                                    scorecardId={scorecardId}
                                    scorecardTypeId={"43"}
                                    isPowerPointPrint={false}
                                    isStacked={true}
                                    scorecard={scorecard}
                                    isIncomeDifferentialDashboard={false}
                                    isEEPlanTrackingDashboard={true}
                                    currentDate={currentDate}
                                    eePlanDateList={eePlanDateList}
                                    filterByEePlanDate={true}
                                />
                            </Route>
                        </Switch>
                    </Grid>
                </Grid>
            </>
        }
    </>);
}